// Which of this session's reads are being served from disk without a fresh
// snapshot behind them. #207's U6, and the thing that makes a stale read
// *visible* rather than merely marked in a value nobody renders.
//
// ── Why a registry and not a prop ────────────────────────────────────────
//
// `useCollectionWindow` already carries `readState` out of every provider, but
// the question a teacher has is **"is what I am looking at current?"**, and the
// answer spans every provider in its own context. Threading it up would mean
// every screen subscribing to every provider it transitively renders — a
// maintained list, which #169 rejected in `outstandingWrites.js`.
//
// So, as there: the two window hooks report here as they read, one entry per
// scope, and the sync chip asks this instead of asking providers. A provider
// reports by calling the hook it reads through, so there is no second list to
// keep up to date.
//
// ── In memory, unlike `outstandingWrites` ────────────────────────────────
//
// That file is `localStorage` because a write must outlive the tab that made
// it. Here `fresh` means *a snapshot landed in this session*, and a session is
// a page load — the same decision as `appStore`'s `refreshedThisSession` set.
// It is per tab rather than per origin for the same reason: another tab having
// heard from the server is not this tab's listener having heard from it.
//
// ── What it does not do ──────────────────────────────────────────────────
//
// **Nothing here blocks anything** (#207 Q5). It counts, it reports, and no
// caller of it refuses a render. The sync chip is a statement about the data,
// not a gate in front of it.
//
// It also says nothing about *which* collection is stale: a chip has room for
// one sentence, and a screen that wants to be specific has `readState` in its
// own provider.

import { READ_STATE } from './appStore';

/** scope key → `true` while that scope is being served without a fresh snapshot. */
const stale = new Map();

const listeners = new Set();

function announce() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      // One bad listener must not stop the others being told. Reported rather
      // than swallowed, so a bug in a subscriber is visible.
      console.error('[staleReads] a listener threw:', err);
    }
  });
}

/**
 * Report what one scope's read state is now.
 *
 * `scope` is any stable string identifying the read — the two window hooks pass
 * `users/{uid}/{collection}` and `users/{uid}/{collection}/{id}`, which are
 * `appStore`'s own scope keys, so two hooks watching one scope agree by
 * construction rather than by convention.
 *
 * `state` is a `READ_STATE`, or `null` for a read that has not come back yet.
 * **Only `stale` counts.** `loading` is not stale — nothing is being shown from
 * disk, because there is nothing on disk; it is the first load, which the app
 * already has a spinner for. Saying "showing saved data" over an empty screen
 * would be false in the direction that matters.
 */
export function reportRead(scope, state) {
  const was = stale.has(scope);
  const is = state === READ_STATE.stale;
  if (was === is) return;
  if (is) stale.set(scope, true);
  else stale.delete(scope);
  announce();
}

/** A scope that is no longer being read at all — an unmounted provider. */
export function forgetRead(scope) {
  if (!stale.delete(scope)) return;
  announce();
}

/** How many scopes are being served from disk without a fresh snapshot. */
export function staleReadCount() {
  return stale.size;
}

/** The scopes themselves, for a diagnostic rather than for a screen. */
export function staleReadScopes() {
  return [...stale.keys()];
}

/** Subscribe to the count changing. Returns an unsubscribe. */
export function onStaleReadsChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test seam. */
export function __resetStaleReadsForTests() {
  stale.clear();
  listeners.clear();
}
